/*
 * Copyright (c) 2018 Helvia Technologies PC <contact@helvia.io>
 */

// Core imports
import React, { Fragment, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

// Component imports
import { Button, Col, Input, Modal, Row, Space, Table, Tag, Tooltip } from 'antd';
import { CopyOutlined, EyeOutlined, SearchOutlined } from '@ant-design/icons';
import BotCloneForm from '../Forms/BotCloneForm';
import BotAvatar from '../UI/BotAvatar';
import TableCellTypography from '../UI/TableCellTypography';
import ResponsiveTagsList from '../UI/ResponsiveTagsList';
import CreateBotModal from '../Modals/CreateBotModal';
import BotContentReplaceModal from '../Modals/BotContentReplaceModal';
import { ReplaceContentIcon } from '../Icons/ReplaceContentIcon';
import { EditIcon } from '../Icons/EditIcon';

// Misc imports
import { NAV_BOT } from '../../constants/navigation';
import { POWERUPS, PERMISSIONS_ORG, DEFAULT_DATETIME_FORMAT, DEFAULT_PAGINATION_SIZE } from '../../constants/main';
import { getCurrentQueryState } from '../../store/utils/getCurrentQueryState';
import { useDragDetection } from '../../utils/hooks/useDragDetection';
import replaceUrlParams from '../../utils/string/replaceUrlParams';
import userHasEditAccess from '../../utils/misc/hasEditAccess';
import useTableFilterIcon from '../../utils/hooks/useTableFilterIcon';

// Constant declarations
let debounceTimeout;

const BotsTable = () => {
  const { orgId } = useParams();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { handleMouseDown, isDragging } = useDragDetection();

  const { data: bots, isLoading: isLoadingBots } = getCurrentQueryState('fetchBots', { orgId });
  const { currentUser } = useSelector((state) => state.auth);
  const { organizationRoles, tenantRoles } = useSelector((state) => state.auth.currentUser);

  const [sortedDataSource, setSortedDataSource] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedBot, setSelectedBot] = useState(null);
  const [isCloneModalOpen, setIsCloneModalOpen] = useState(false);
  const [isCreateBotModalOpen, setIsCreateBotModalOpen] = useState(false);
  const [isCopyBotContentsModalOpen, setIsCopyBotContentsModalOpen] = useState(false);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGINATION_SIZE);

  const isOrgAdmin = currentUser?.effectiveRoles?.organizationRoles[orgId]?.includes(PERMISSIONS_ORG.ADMIN);

  const canEditBot = (tenantId) => userHasEditAccess(orgId, organizationRoles, tenantId, tenantRoles);

  const columns = [
    {
      title: t('Agent Name'),
      key: 'name',
      sorter: (a, b) => a.displayName.localeCompare(b.displayName),
      ellipsis: true,
      render: (_text, record) => (
        <Link to={replaceUrlParams(NAV_BOT, { tenantId: record.id, orgId })}>
          <BotAvatar bot={record} avatarSize={24} />
        </Link>
      )
    },
    {
      title: t('Description'),
      dataIndex: 'description',
      key: 'description',
      responsive: ['sm'],
      ellipsis: { showTitle: false },
      render: text => (
        <TableCellTypography>
          {text || '-'}
        </TableCellTypography>
      )
    },
    {
      title: t('Date Created'),
      dataIndex: 'created',
      key: 'created',
      responsive: ['xxl'],
      width: 200,
      sorter: (a, b) => moment(a.created).isSameOrAfter(b.created) ? 1 : -1,
      render: value => moment(value).format(DEFAULT_DATETIME_FORMAT)
    },
    {
      title: t('Powerups'),
      dataIndex: 'powerups',
      key: 'powerups',
      filters:
        Object.entries(POWERUPS)
          .map(([key, value]) => ({
            text: (
              <Space size={2}>
                <Tag
                  color="blue"
                  className="border-0 border-radius-base py-1 px-2"
                >
                  {value.icon}
                </Tag>

                {t(key, { context: 'powerup' })}
              </Space>
            ),
            value: key
          })),
      onFilter: (value, record) => record.powerups.includes(value),
      filterIcon: useTableFilterIcon,
      width: 200,
      responsive: ['lg'],
      render: (_text, record) => {
        const availablePowerups = Object.entries(POWERUPS).filter(powerup => record.powerups.some(p => p === powerup[1].key));

        if (!availablePowerups.length) {
          return '-';
        }

        return (
          <ResponsiveTagsList
            values={
              availablePowerups.map(([key, value]) => ({
                icon: value.icon,
                label: t(key, { context: 'powerup' }),
                value: key
              }))
            }
            maxTagCount={4}
            tagRender={(tag) => {
              const matchedTag = availablePowerups.find(([key]) => key === tag.value)[1];
              return (
                <Tooltip overlayClassName="pointer-events-none" title={t(matchedTag.key, { context: 'powerup' })}>
                  <span className="mr-3">{matchedTag.icon}</span>
                </Tooltip>
              );
            }}
          />
        );
      }
    },
    {
      title: t('Tags'),
      dataIndex: 'tags',
      key: 'tags',
      responsive: ['xl'],
      width: 200,
      ellipsis: true,
      render: (values) => {
        if (!values || !values.length) {
          return '-';
        }

        return (
          <ResponsiveTagsList
            values={values.map((value, index) => ({ label: value, value: index }))}
            tagRender={(tag) => <Tag>{tag.label}</Tag>}
          />
        );
      }
    },
    {
      title: t('Agent Availability'),
      key: 'availability',
      dataIndex: 'disabled',
      responsive: ['lg'],
      width: 160,
      filters: [
        {
          text: 'Enabled',
          value: false
        },
        {
          text: 'Disabled',
          value: true
        }
      ],
      onFilter: (value, record) => value ? record.disabled : !record.disabled,
      filterIcon: useTableFilterIcon,
      render: value => <Tag className="fw-500" color={value ? 'error' : 'success'}>{value ? 'Disabled' : 'Enabled'}</Tag>
    },
    {
      title: t('Actions'),
      key: 'actions',
      align: 'center',
      width: 140,
      render: (_text, record) => (
        <Space size={4}>
          <Tooltip overlayClassName="pointer-events-none" title={t('View agent')}>
            <Link to={replaceUrlParams(NAV_BOT, { tenantId: record.id, orgId })}>
              <Button
                type="text"
                icon={canEditBot(record.id) ? <EditIcon /> : <EyeOutlined />}
                shape="circle"
                onClick={(event) => event.stopPropagation()}
              />
            </Link>
          </Tooltip>

          {
            isOrgAdmin ? (
              <Tooltip overlayClassName="pointer-events-none" title={t('Clone agent')}>
                <Button
                  type="text"
                  shape="circle"
                  icon={<CopyOutlined />}
                  onClick={(event) => {
                    event.stopPropagation();
                    openCloneModal(record);
                  }}
                />
              </Tooltip>
            ) : null
          }

          {
            canEditBot(record.id) ? (
              <Tooltip overlayClassName="pointer-events-none" title={t('Replace Agent Content')}>
                <Button
                  type="text"
                  icon={<ReplaceContentIcon />}
                  shape="circle"
                  onClick={(event) => {
                    event.stopPropagation();
                    openCopyBotContentsModal(record);
                  }}
                />
              </Tooltip>
            ) : null
          }
        </Space>
      )
    }
  ];

  useEffect(() => {
    if (!bots || !bots.items) {
      return;
    }

    const dataSource = [...bots.items];
    setSortedDataSource(
      dataSource.sort((a, b) => {
        return a.displayName.localeCompare(b.displayName);
      })
        .filter(bot => search ? bot.description?.toLowerCase().includes(search.toLowerCase()) || bot.displayName?.toLowerCase().includes(search.toLowerCase()) : true)
    );
  }, [bots, search]);

  const openCloneModal = (record) => {
    setSelectedBot(record);
    setIsCloneModalOpen(true);
  };
  const closeCloneModal = () => {
    setIsCloneModalOpen(false);
  };

  const openCreateBotModal = () => {
    setIsCreateBotModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsCreateBotModalOpen(false);
  };

  const openCopyBotContentsModal = (record) => {
    setSelectedBot(record);
    setIsCopyBotContentsModalOpen(true);
  };
  const closeCopyBotContentsModal = () => {
    setIsCopyBotContentsModalOpen(false);
  };

  const handleTableChange = (pagination, _filters, _sorter) => {
    setPageSize(pagination.pageSize);
  };

  return (
    <Fragment>
      <Row className="mb-4">
        <Col span={4}>
          <Input
            prefix={<SearchOutlined className="text-secondary" />}
            size="large"
            placeholder={t('Search in contents')}
            allowClear
            onChange={({ target }) => {
              clearTimeout(debounceTimeout);
              debounceTimeout = setTimeout(() => {
                setSearch(target.value.trim());
              }, 50);
            }}
          />
        </Col>

        <Col span={12} offset={8}>
          <Button onClick={() => openCreateBotModal()} className="float-right" type="primary" size="large">
            {t('Create New Agent')}
          </Button>
        </Col>
      </Row>

      <Table
        rowKey="id"
        rowClassName="cursor-pointer"
        onRow={(record) => ({
          onMouseDown: handleMouseDown,
          onClick: () => {
            if (!isDragging()) {
              navigate(replaceUrlParams(NAV_BOT, { tenantId: record.id, orgId }));
            }
          }
        })}
        columns={columns}
        size="small"
        pagination={{
          size: 'default',
          defaultCurrent: 1,
          pageSize: pageSize,
          showSizeChanger: true,
          showTotal: (total) => `${t('Total Agents')}: ${total}`
        }}
        dataSource={sortedDataSource}
        loading={isLoadingBots}
        onChange={handleTableChange}
      />

      <Modal
        title={t('Clone Agent')}
        open={isCloneModalOpen}
        onCancel={closeCloneModal}
        footer={null}
        style={{ maxWidth: 600 }}
        width={'95%'}
        centered
      >
        <BotCloneForm botToClone={selectedBot} onSuccess={closeCloneModal} />
      </Modal>

      <CreateBotModal isModalOpen={isCreateBotModalOpen} closeModal={closeDeleteModal} />

      <BotContentReplaceModal isModalOpen={isCopyBotContentsModalOpen} closeModal={closeCopyBotContentsModal} targetBot={selectedBot} />
    </Fragment>
  );
};

export default BotsTable;
